'use client';

import { SessionProvider } from 'next-auth/react';
import React, { useEffect, useRef } from 'react';
import { usePlan } from '@/features/subscription/hooks/usePlan';
import useAuth from '@/hooks/useAuth';
import { usePayment } from '@/features/subscription/hooks/usePayment';

const PlanRefreshGate = () => {
  const { user } = useAuth();
  const { refreshData, planIsActive } = usePlan();
  const { paymentStatus } = usePayment();
  const inFlightRef = useRef(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const loginRefreshUserRef = useRef<string | null>(null);

  useEffect(() => {
    const runRefresh = async () => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        await refreshData({ withUserRefresh: true });
      } finally {
        inFlightRef.current = false;
      }
    };

    if (!user) {
      loginRefreshUserRef.current = null;
    } else if (!planIsActive && loginRefreshUserRef.current !== user.uid) {
      loginRefreshUserRef.current = user.uid;
      void runRefresh();
    }

    if (user && paymentStatus === 'completed') {
      void runRefresh();

      if (!planIsActive && !intervalRef.current) {
        intervalRef.current = setInterval(() => {
          void runRefresh();
        }, 10_000);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [user?.uid, paymentStatus, planIsActive, refreshData]);

  useEffect(() => {
    if (planIsActive && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [planIsActive]);

  return null;
};

export default function NextAuthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <SessionProvider>
      <PlanRefreshGate />
      {children}
    </SessionProvider>
  );
}
